import React, { useState } from "react";
import {
    makeStyles,
    Button,
    TextField,
    Typography,
    Card,
    CardContent,
    InputAdornment,
    CircularProgress,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
    Email,
    Lock,
    VpnKey,
} from "@material-ui/icons";
import { withRouter, Link } from "react-router-dom";
import logoImg from "../assets/images/logo.png";
import { sendVerificationCode, verifyCode, companyResetPassword as resetPassword } from "../actions/auth";
import { generateStrongPassword } from "../utils/passwordGenerator";

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "relative",
        overflow: "hidden",
    },
    container: {
        position: "relative",
        zIndex: 1,
        padding: theme.spacing(4),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
    },
    card: {
        maxWidth: 450,
        width: "100%",
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(20px)",
        borderRadius: theme.spacing(3),
        boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        padding: theme.spacing(4),
    },
    logo: {
        maxHeight: 80,
        marginBottom: theme.spacing(3),
        display: "block",
        margin: "0 auto",
    },
    title: {
        fontWeight: 700,
        textAlign: "center",
        marginBottom: theme.spacing(1),
        color: "rgb(20,61,89)",
    },
    subtitle: {
        textAlign: "center",
        color: "#6b7280",
        marginBottom: theme.spacing(4),
    },
    submitButton: {
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "#fff",
        padding: theme.spacing(1.5, 4),
        borderRadius: theme.spacing(3),
        fontWeight: 600,
        marginTop: theme.spacing(2),
        width: "100%",
        "&:hover": {
            background: "linear-gradient(135deg, #5568d3 0%, #6a4190 100%)",
        },
    },
    textField: {
        marginBottom: theme.spacing(2),
    },
    linkText: {
        display: "block",
        textAlign: "center",
        marginTop: theme.spacing(3),
        color: "#667eea",
        textDecoration: "none",
        fontWeight: 600,
    },
}));

const CompanyForgotPassword = ({ history }) => {
    const classes = useStyles();
    const [step, setStep] = useState(1); // 1: Email, 2: Code, 3: New Password
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [generatedPassword, setGeneratedPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    const handleSendCode = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const data = await sendVerificationCode(email);
            if (data.err) {
                setError(data.err);
            } else {
                setMessage(data.message);
                setStep(2);
            }
        } catch (err) {
            setError("Failed to send code. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const data = await verifyCode(email, code);
            if (data.err) {
                setError(data.err);
            } else {
                setStep(3);
            }
        } catch (err) {
            setError("Verification failed.");
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!newPassword || !confirmPassword) {
            setError("New password and retype new password are required.");
            return;
        }
        if (newPassword.length < 6) {
            setError("New password must be at least 6 characters.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setError("New password and retype new password do not match.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const data = await resetPassword({ email, newPassword, code });
            if (data.err) {
                setError(data.err);
            } else {
                setMessage("Password reset successful. Redirecting to sign in...");
                setTimeout(() => {
                    history.push("/company/signin");
                }, 3000);
            }
        } catch (err) {
            setError("Failed to reset password.");
        } finally {
            setLoading(false);
        }
    };

    const handleGeneratePassword = () => {
        const generated = generateStrongPassword(16);
        setGeneratedPassword(generated);
        setError("");
    };

    const handleAcceptGeneratedPassword = () => {
        if (!generatedPassword) return;
        setNewPassword(generatedPassword);
        setConfirmPassword(generatedPassword);
        setGeneratedPassword("");
    };

    const handleRejectGeneratedPassword = () => {
        setGeneratedPassword("");
    };

    return (
        <div className={classes.root}>
            <div className={classes.container}>
                <Card className={classes.card}>
                    <img src={logoImg} className={classes.logo} alt="Logo" />
                    <CardContent>
                        <Typography variant="h5" className={classes.title}>
                            {step === 1 && "Forgot Password"}
                            {step === 2 && "Verify Code"}
                            {step === 3 && "Reset Password"}
                        </Typography>
                        <Typography variant="body2" className={classes.subtitle}>
                            {step === 1 && "Enter your email to receive a 6-digit code."}
                            {step === 2 && "Enter the 6-digit code sent to your email."}
                            {step === 3 && "Enter your new password."}
                        </Typography>

                        {error && <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>}
                        {message && <Alert severity="success" style={{ marginBottom: 16 }}>{message}</Alert>}

                        {step === 1 && (
                            <form onSubmit={handleSendCode}>
                                <TextField
                                    fullWidth
                                    label="Company Email Address"
                                    variant="outlined"
                                    required
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className={classes.textField}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Email color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={loading}
                                    className={classes.submitButton}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : "Send Code"}
                                </Button>
                            </form>
                        )}

                        {step === 2 && (
                            <form onSubmit={handleVerifyCode}>
                                <TextField
                                    fullWidth
                                    label="6-Digit Verification Code"
                                    variant="outlined"
                                    required
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    className={classes.textField}
                                    inputProps={{ maxLength: 6 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <VpnKey color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={loading}
                                    className={classes.submitButton}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : "Verify Code"}
                                </Button>
                            </form>
                        )}

                        {step === 3 && (
                            <form onSubmit={handleResetPassword}>
                                <TextField
                                    fullWidth
                                    label="New Password"
                                    variant="outlined"
                                    required
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className={classes.textField}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    label="Retype New Password"
                                    variant="outlined"
                                    required
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className={classes.textField}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleGeneratePassword}
                                    >
                                        Generate Password
                                    </Button>
                                    {generatedPassword ? (
                                        <>
                                            <Button type="button" variant="outlined" color="primary" onClick={handleAcceptGeneratedPassword}>
                                                Accept
                                            </Button>
                                            <Button type="button" variant="outlined" color="secondary" onClick={handleRejectGeneratedPassword}>
                                                Reject
                                            </Button>
                                        </>
                                    ) : null}
                                </div>
                                {generatedPassword ? (
                                    <Alert severity="info" style={{ marginBottom: 16, wordBreak: "break-all" }}>
                                        {generatedPassword}
                                    </Alert>
                                ) : null}
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={loading}
                                    className={classes.submitButton}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : "Reset Password"}
                                </Button>
                            </form>
                        )}

                        <Link to="/company/signin" className={classes.linkText}>
                            Back to Sign In
                        </Link>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default withRouter(CompanyForgotPassword);
